import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { getFixtureProviders } from '../fixtures';

interface FiltersContextValue {
  selectedProviderIds: Set<string>;
  isProviderSelected: (providerId: string) => boolean;
  toggleProvider: (providerId: string) => void;
  selectAllProviders: () => void;
  selectOnlyProvider: (providerId: string) => void;
  showUnassigned: boolean;
  toggleUnassigned: () => void;
}

const Ctx = createContext<FiltersContextValue | null>(null);

export const ExperimentalCalendarFiltersProvider = ({ children }: { children: ReactNode }) => {
  // Default: all providers visible.
  const [selectedProviderIds, setSelectedProviderIds] = useState<Set<string>>(
    () => new Set(getFixtureProviders().map((p) => p.provider_id)),
  );
  const [showUnassigned, setShowUnassigned] = useState(true);

  const toggleProvider = useCallback((providerId: string) => {
    setSelectedProviderIds((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  }, []);

  const selectAllProviders = useCallback(() => {
    setSelectedProviderIds(new Set(getFixtureProviders().map((p) => p.provider_id)));
  }, []);

  const selectOnlyProvider = useCallback((providerId: string) => {
    setSelectedProviderIds(new Set([providerId]));
  }, []);

  const toggleUnassigned = useCallback(() => setShowUnassigned((v) => !v), []);

  const value = useMemo<FiltersContextValue>(
    () => ({
      selectedProviderIds,
      isProviderSelected: (id: string) => selectedProviderIds.has(id),
      toggleProvider,
      selectAllProviders,
      selectOnlyProvider,
      showUnassigned,
      toggleUnassigned,
    }),
    [
      selectedProviderIds,
      toggleProvider,
      selectAllProviders,
      selectOnlyProvider,
      showUnassigned,
      toggleUnassigned,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useExperimentalCalendarFilters = (): FiltersContextValue => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      'useExperimentalCalendarFilters must be used inside ExperimentalCalendarFiltersProvider',
    );
  }
  return ctx;
};
